<template>
  <v-container fluid class="pa-0">
    <!-- Header section -->
    <v-sheet color="primary" class="py-8 mb-8" rounded="0" elevation="1">
      <v-row justify="center" class="text-center mt-2">
        <v-icon icon="mdi-account-edit-outline" size="56" color="white" class="mb-4"></v-icon>
        <h1 class="text-h3 font-weight-light text-white">{{ t('personaedit.title') }}</h1>
      </v-row>
      <v-row justify="center" class="text-center">
        <div class="text-subtitle-1 text-white mt-2">{{ t('personaedit.description') }}</div>
      </v-row>
    </v-sheet>

    <v-container>
      <div class="d-flex flex-wrap justify-space-between align-center mb-6 ga-3">
        <v-btn
            color="primary"
            variant="tonal"
            @click="goBack"
            prepend-icon="mdi-arrow-left"
            rounded="pill"
            density="comfortable"
            class="elevation-1"
        >
          {{ t('personaedit.backToPersonaManagement') }}
        </v-btn>

        <div class="d-flex ga-3" v-if="!loading && persona">
          <v-btn variant="outlined" color="#0D3B6E" rounded="pill" @click="goBack">{{ t('generic.cancel') }}</v-btn>
          <v-btn
              color="primary"
              variant="flat"
              rounded="pill"
              prepend-icon="mdi-content-save-outline"
              :loading="saving"
              @click="save"
          >
            {{ t('personamanagement.saveChanges') }}
          </v-btn>
        </div>
      </div>

      <div v-if="loading" class="d-flex justify-center py-12">
        <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
      </div>

      <v-alert v-else-if="!persona" type="error" variant="tonal">
        {{ t('personaedit.notFound') }}
      </v-alert>

      <div v-else class="edit-wrapper">
        <!-- Left: profile image + form -->
        <div>
          <v-card variant="outlined" rounded="lg" class="pa-4 mb-4 text-center">
            <v-avatar size="120" rounded="circle" class="persona-avatar mb-3">
              <v-img :src="form.image || genericPersonaImage" cover :alt="displayName"></v-img>
            </v-avatar>
            <div>
              <v-btn variant="outlined" color="primary" size="small" prepend-icon="mdi-image-edit-outline" @click="imagePickerOpen = true">
                {{ t('personaedit.changeImage') }}
              </v-btn>
            </div>
          </v-card>

          <v-card variant="outlined" rounded="lg" class="pa-4">
            <v-form ref="formRef" v-model="formValid">
              <div class="form-grid">
                <div v-for="field in fieldDefs" :key="field.name">
                  <label class="field-label mb-1 d-block">
                    {{ fieldLabel(field.name) }}<span v-if="isRequired(field.name)" class="text-red"> *</span>
                  </label>

                  <v-combobox
                      v-if="field.name === 'active_services'"
                      v-model="form.active_services"
                      multiple
                      chips
                      closable-chips
                      :placeholder="t('personaedit.activeServicesPlaceholder')"
                      :rules="fieldRules(field.name)"
                      :readonly="!isFieldEditable(field.name)"
                      variant="outlined"
                      density="comfortable"
                      hide-details="auto"
                  />

                  <v-select
                      v-else-if="selectItemsFor(field)"
                      v-model="form[field.name]"
                      :items="selectItemsFor(field)"
                      clearable
                      :rules="fieldRules(field.name)"
                      :readonly="!isFieldEditable(field.name)"
                      variant="outlined"
                      density="comfortable"
                      hide-details="auto"
                  />

                  <v-text-field
                      v-else-if="isNumberField(field)"
                      v-model.number="form[field.name]"
                      type="number"
                      :min="numberFieldBounds(field).min"
                      :max="numberFieldBounds(field).max"
                      :suffix="numberFieldUnit(field)"
                      :rules="fieldRules(field.name)"
                      :readonly="!isFieldEditable(field.name)"
                      variant="outlined"
                      density="comfortable"
                      hide-details="auto"
                  />

                  <v-text-field
                      v-else
                      v-model="form[field.name]"
                      :rules="fieldRules(field.name)"
                      :readonly="!isFieldEditable(field.name)"
                      variant="outlined"
                      density="comfortable"
                      hide-details="auto"
                  />
                </div>
              </div>
            </v-form>
          </v-card>
        </div>

        <!-- Right: live preview -->
        <v-card variant="outlined" rounded="lg" class="preview-card">
          <v-card-title class="d-flex align-center py-4 px-4">
            <v-icon icon="mdi-eye-outline" color="primary" class="mr-2" size="20"></v-icon>
            <span class="text-subtitle-1 font-weight-bold">{{ t('personaedit.personaPreview') }}</span>
          </v-card-title>
          <v-divider></v-divider>

          <div class="text-center pa-4">
            <v-avatar size="88" rounded="circle" class="persona-avatar mb-2">
              <v-img :src="form.image || genericPersonaImage" cover :alt="displayName"></v-img>
            </v-avatar>
            <div class="text-h6 font-weight-bold">{{ displayName || t('personaedit.unnamedPersona') }}</div>
            <div class="text-body-2 text-medium-emphasis">{{ previewSubtitle }}</div>
          </div>
          <v-divider></v-divider>

          <div class="preview-list pa-4">
            <div v-for="field in fieldDefs" :key="'preview-' + field.name" class="preview-item">
              <p class="text-caption text-primary font-weight-medium mb-0" style="text-transform: uppercase">{{ fieldLabel(field.name) }}</p>
              <p class="text-body-2 font-weight-medium mb-0">{{ previewValue(field.name) }}</p>
            </div>
          </div>
        </v-card>
      </div>
    </v-container>

    <!-- Change Image picker -->
    <v-dialog v-model="imagePickerOpen" max-width="640">
      <v-card>
        <v-card-title class="text-h6">{{ t('personaedit.changeImage') }}</v-card-title>
        <v-card-text>
          <div class="image-picker-grid">
            <v-avatar
                v-for="path in imageCatalog"
                :key="path"
                size="72"
                rounded="circle"
                class="image-picker-option"
                :class="{ 'image-picker-option--selected': form.image === path }"
                @click="selectImage(path)"
            >
              <v-img :src="path" cover></v-img>
            </v-avatar>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="imagePickerOpen = false">{{ t('generic.close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="errorSnackbar" color="error" location="bottom right" :timeout="4000">
      <v-icon class="mr-2">mdi-alert-circle-outline</v-icon>{{ errorMessage }}
    </v-snackbar>
  </v-container>
</template>

<script>
import {
  fetch_persona_domain_schema,
  fetch_personas_by_domain,
  fetch_generated_personas,
  create_or_update_persona,
} from "@/api.js";
import { useI18n } from "vue-i18n";
import { mergeSelectChoicesWithValue } from '@/utils/attributeValueCoercion';
import { canonicalizePersonaSelectValues, canonicalizeSelectValue, expandSelectChoices } from '@/utils/selectChoices';
import {
  buildNumberFieldRules,
  resolveNumberFieldBounds,
} from '@/utils/personaFieldValidation';
import {
  loadStaticPersonasForLookup,
  normalizeAssetReference,
  resolvePersonaImage,
  staticAssetPath,
} from '@/utils/personaDomainLoader';

// Same fixed catalog already used as fallback images across PersonaGrid.vue /
// PersonaManagement.vue / PersonaInfo.vue — there is no upload capability or
// server-side manifest for persona images, so "Change Image" picks from this
// existing set rather than inventing new upload infrastructure.
const IMAGE_CATALOG = [
  "Mariko.png", "Sasaki.png", "Takanaka.png",
  "female_21_student.jpg", "female_31_nurse.jpg", "female_32_manager.jpg",
  "female_34_engineer.jpg", "female_40_freelancer.jpg", "female_45_doctor.jpg",
  "female_48_employed.jpg", "female_70_retired.jpg",
  "male_18_student.jpg", "male_22_student.jpg", "male_27_employed.jpg",
  "male_27_engineer.jpg", "male_28_employed.jpg", "male_37_manager.jpg",
  "male_39_freelancer.jpg", "male_42_freelancer.jpg", "male_44_employed.jpg",
  "male_51_teacher.jpg", "male_58_doctor.jpg", "male_77_retired.jpg",
].map((f) => staticAssetPath(`/imgs/personas_pics/${f}`));

// Same external placeholder PersonaGrid.vue's "Generated Personas" cards
// already fall back to for a persona with no image of its own — used here
// only for display, never persisted (see getPersonaImage below).
const GENERIC_PERSONA_IMAGE = 'https://randomuser.me/api/portraits/lego/5.jpg';

// Form field name -> possible persona-schema attribute keys (and vice versa).
const ATTR_KEY_ALIASES = {
  job_title: ['job_title', 'occupation'],
  occupation: ['occupation', 'job_title'],
  marital_status: ['marital_status', 'civil_status', 'civilStatus'],
  civil_status: ['civil_status', 'marital_status', 'civilStatus'],
  education_level: ['education_level', 'qualification'],
  qualification: ['qualification', 'education_level'],
  annual_income: ['annual_income', 'income'],
  income: ['income', 'annual_income'],
  area: ['area', 'geographic_area_of_residence', 'residence'],
  geographic_area_of_residence: ['geographic_area_of_residence', 'area', 'residence'],
  interests: ['interests', 'personal_interests', 'personalInterests'],
  personal_interests: ['personal_interests', 'interests', 'personalInterests'],
  segments: ['segments', 'segmentation', 'segment'],
};

// Schema keys that Edit Persona already maps onto a preferred form field name.
const SCHEMA_KEY_TO_FORM_NAME = {
  occupation: 'job_title',
  civil_status: 'marital_status',
  civilStatus: 'marital_status',
  qualification: 'education_level',
  income: 'annual_income',
  geographic_area_of_residence: 'area',
  residence: 'area',
  personal_interests: 'interests',
  personalInterests: 'interests',
};

// Hardcoded after the dynamic field grid (legacy image block only).
const HARDCODED_EDIT_FIELDS = new Set(['image']);

export default {
  name: "PersonaEditPersona",
  props: {
    personaId: { type: String, required: true },
  },
  setup() {
    const { t } = useI18n();
    return { t };
  },
  data() {
    return {
      domain: "",
      loading: true,
      saving: false,
      persona: null, // resolved row (name/image/raw doc)
      fieldDefs: [],
      // Domain attribute key -> Editable / Required toggles from Persona
      // Framework Editor. A key absent here has no domain-schema entry, so
      // helpers fall back to legacy defaults (editable=true; required only
      // for the original hardcoded first_name/age/job_title set).
      domainAttrEditable: {},
      domainAttrRequired: {},
      // Domain attribute key -> { type, options } from Framework Editor so
      // Single Select fields render as dropdowns with the configured choices.
      domainAttrMeta: {},
      // Domain attribute key / form name -> display label from Framework Editor.
      domainAttrLabels: {},
      form: {
        image: "",
      },
      formValid: true,
      rules: {
        required: (value) => {
          if (Array.isArray(value)) {
            return value.length > 0 || this.t('personaedit.fieldRequired');
          }
          if (value === null || value === undefined) {
            return this.t('personaedit.fieldRequired');
          }
          if (typeof value === 'number') {
            return !Number.isNaN(value) || this.t('personaedit.fieldRequired');
          }
          return String(value).trim() !== '' || this.t('personaedit.fieldRequired');
        },
      },
      imagePickerOpen: false,
      imageCatalog: IMAGE_CATALOG,
      genericPersonaImage: GENERIC_PERSONA_IMAGE,
      errorSnackbar: false,
      errorMessage: "",
    };
  },
  computed: {
    displayName() {
      const first = this.form.first_name || this.form.name || '';
      const last = this.form.last_name || this.form.surname || '';
      return `${first} ${last}`.trim();
    },
    previewSubtitle() {
      return this.form.job_title || this.form.occupation || '';
    },
  },
  async mounted() {
    this.domain = this.$route.query.domain || "";
    // Schema first so populateForm / selectItemsFor can honor Single Select
    // types (e.g. age) before the form is filled.
    await this.loadFieldDefs();
    await this.loadPersona();
    this.loading = false;
  },
  methods: {
    goBack() {
      this.$router.push({ name: 'persona-management', query: { domain: this.domain } });
    },

    async loadFieldDefs() {
      if (!this.domain) {
        this.fieldDefs = [];
        return;
      }

      try {
        const response = await fetch_persona_domain_schema(this.domain);
        const attributes = [...(response.data?.attributes || [])].sort(
          (a, b) => (a.display_order || 0) - (b.display_order || 0),
        );

        this.domainAttrEditable = Object.fromEntries(
          attributes.map((attr) => [attr.key, attr.editable !== false]),
        );
        this.domainAttrRequired = Object.fromEntries(
          attributes.map((attr) => [attr.key, !!attr.required]),
        );
        this.domainAttrMeta = Object.fromEntries(
          attributes.map((attr) => [attr.key, { type: attr.type, options: attr.options || null }]),
        );
        this.domainAttrLabels = Object.fromEntries(
          attributes
            .filter((attr) => attr.key && attr.label)
            .map((attr) => [attr.key, attr.label]),
        );

        this.fieldDefs = attributes
          .filter((attr) => attr.key && !HARDCODED_EDIT_FIELDS.has(attr.key))
          .map((attr) => this.schemaAttributeToFieldDef(attr));
      } catch (error) {
        console.error('Failed to load persona domain schema:', error);
        this.fieldDefs = [];
      }
    },

    schemaAttributeToFieldDef(attr) {
      const choices = attr.type === 'single_select'
        ? expandSelectChoices(attr.options?.choices)
        : [];
      return {
        name: attr.key,
        schema_key: attr.key,
        type: attr.type === 'number' ? 'number' : (attr.type === 'single_select' ? 'array' : 'string'),
        range: choices.length ? choices : null,
        mandatory: !!attr.required,
        label: attr.label || attr.key,
        min: attr.options?.min ?? attr.options?.min_value ?? undefined,
        max: attr.options?.max ?? attr.options?.max_value ?? undefined,
        unit: typeof attr.options?.unit === 'string' ? attr.options.unit.trim() : '',
      };
    },

    formNameForSchemaKey(schemaKey) {
      return SCHEMA_KEY_TO_FORM_NAME[schemaKey] || schemaKey;
    },

    isNumberField(field) {
      if (!field) return false;
      if (field.type === 'number') return true;
      const meta = this.getDomainAttrMeta(field.name);
      return meta?.type === 'number';
    },

    numberFieldUnit(field) {
      const fromField = typeof field?.unit === 'string' ? field.unit.trim() : '';
      if (fromField) return fromField;
      const meta = this.getDomainAttrMeta(field?.name);
      const fromMeta = typeof meta?.options?.unit === 'string' ? meta.options.unit.trim() : '';
      return fromMeta || undefined;
    },
    getDomainAttrMeta(name) {
      const candidates = this.schemaLookupKeys(name);
      for (const key of candidates) {
        if (Object.prototype.hasOwnProperty.call(this.domainAttrMeta, key)) {
          return this.domainAttrMeta[key];
        }
      }
      return null;
    },

    isDomainSingleSelect(name) {
      return this.getDomainAttrMeta(name)?.type === 'single_select';
    },

    // Prefer Framework Editor Single Select choices; otherwise legacy
    // fields_schema range. Returns null when the field is not a dropdown.
    // Age follows the domain schema type (number by default).
    selectItemsFor(field) {
      const meta = this.getDomainAttrMeta(field.name);
      const storedValue = this.form?.[field.name];
      if (meta?.type === 'single_select') {
        const choices = mergeSelectChoicesWithValue(meta.options?.choices, storedValue);
        if (choices.length) {
          return choices;
        }
      }
      if (field.name === 'age') return null;
      if (Array.isArray(field.range) && field.range.length) {
        return mergeSelectChoicesWithValue(field.range, storedValue);
      }
      return null;
    },

    // Align a stored value with Single Select choice strings (e.g. age 20 → "20")
    // so v-select can match an item. Leaves values untouched when not single_select.
    normalizeSelectValue(fieldName, value) {
      if (value === null || value === undefined || value === '') return value;
      if (!this.isDomainSingleSelect(fieldName)) return value;
      const items = this.selectItemsFor({ name: fieldName, range: null });
      if (!items || !items.length) return value;
      const canonical = canonicalizeSelectValue(value, items);
      if (canonical !== value) return canonical;
      const asString = String(value);
      if (items.includes(asString)) return asString;
      const matched = items.find((item) => String(item) === asString || Number(item) === Number(value));
      return matched !== undefined ? matched : asString;
    },

    serializeAgeForSave(value) {
      if (value === '' || value === null || value === undefined) return undefined;
      if (this.isDomainSingleSelect('age')) {
        const asNumber = Number(value);
        if (!Number.isNaN(asNumber) && String(value).trim() !== '') {
          return asNumber;
        }
        return value;
      }
      return Number(value);
    },

    fieldLabel(name) {
      if (this.domainAttrLabels[name]) return this.domainAttrLabels[name];
      for (const key of this.schemaLookupKeys(name)) {
        if (this.domainAttrLabels[key]) return this.domainAttrLabels[key];
      }
      const field = this.fieldDefs.find((f) => f.name === name);
      if (field?.label) return field.label;
      const i18nKey = 'personaedit.fields.' + name;
      const translated = this.t(i18nKey);
      return translated !== i18nKey ? translated : name;
    },

    schemaLookupKeys(name) {
      return ATTR_KEY_ALIASES[name] || [name];
    },

    // Required comes from the domain schema toggle. If the schema has no
    // entry for this field (or schema failed to load), fall back to the
    // legacy hardcoded set so existing Telco forms keep their previous
    // mandatory fields.
    isRequired(name) {
      const candidates = this.schemaLookupKeys(name);
      for (const key of candidates) {
        if (Object.prototype.hasOwnProperty.call(this.domainAttrRequired, key)) {
          return !!this.domainAttrRequired[key];
        }
      }
      if (!Object.keys(this.domainAttrRequired).length) {
        return name === 'age' || name === 'job_title' || name === 'first_name';
      }
      return false;
    },

    // Persona Framework Editor's Editable toggle controls whether this
    // attribute's value can be changed here: Editable=ON -> editable,
    // Editable=OFF -> read-only. Independent of Required.
    isFieldEditable(name) {
      const candidates = this.schemaLookupKeys(name);
      for (const key of candidates) {
        if (Object.prototype.hasOwnProperty.call(this.domainAttrEditable, key)) {
          return !!this.domainAttrEditable[key];
        }
      }
      return true;
    },

    numberFieldBounds(field) {
      return resolveNumberFieldBounds(field);
    },

    fieldRules(name) {
      const rules = [];
      if (this.isRequired(name)) {
        rules.push(this.rules.required);
      }
      const field = this.fieldDefs.find((item) => item.name === name);
      if (field && this.isNumberField(field)) {
        rules.push(...buildNumberFieldRules(field, this.t));
      }
      return rules;
    },

    matchesOption(fieldName, value) {
      if (!value) return false;
      const items = this.selectItemsFor({
        name: fieldName,
        range: this.fieldDefs.find((f) => f.name === fieldName)?.range || null,
      });
      return !!(items && items.includes(value));
    },
    parseActiveServices(value) {
      if (Array.isArray(value)) return value.filter(Boolean);
      if (typeof value === 'string') return value.split(',').map((s) => s.trim()).filter(Boolean);
      return [];
    },

    // Optional static catalog for image enrichment only (B5).
    async loadStaticPersonas(domain) {
      return loadStaticPersonasForLookup(domain);
    },
    // Resolves a persona's actual image identity — a real static-derived
    // path, or an explicitly-saved data.image. Returns '' when neither
    // applies rather than fabricating a catalog path — the template applies
    // a display-only placeholder (genericPersonaImage) wherever this value
    // is rendered, so nothing invented here ever gets persisted on save.
    getPersonaImage(persona) {
      return resolvePersonaImage(persona, { fallback: 'empty' });
    },

    async loadPersona() {
      // Fast path: PersonaManagement.vue already resolved this row (name,
      // image, matched static entry) before navigating here.
      let raw = null;
      let resolvedImage = null;
      try {
        const cached = JSON.parse(sessionStorage.getItem('editPersonaRow') || 'null');
        if (cached && cached.persona_id === this.personaId) {
          raw = cached.raw;
          resolvedImage = cached.image;
          this.domain = cached.domain || this.domain;
        }
      } catch (e) {
        raw = null;
      }

      // Fallback: direct load/refresh — re-fetch and re-resolve the same way
      // PersonaManagement.vue's buildRow does (name match, then unambiguous
      // image-anchor match).
      if (!raw) {
        try {
          const [manualRes, generatedRes, staticList] = await Promise.all([
            fetch_personas_by_domain(this.domain),
            fetch_generated_personas(this.domain),
            this.loadStaticPersonas(this.domain).catch(() => []),
          ]);
          const docs = [...(manualRes.data || []), ...(generatedRes.data || [])];
          raw = docs.find((d) => d.persona_id === this.personaId) || null;

          if (raw) {
            const staticByName = new Map(staticList.map((p) => [(p.title || p.name || '').trim().toLowerCase(), p]));
            // Static personas already correctly claimed by a different
            // backend document via name match aren't eligible as an
            // image-fallback candidate for this one (see
            // PersonaManagement.vue's buildRow for the full rationale).
            const claimedNames = new Set(
                docs.map((d) => (d.name || '').trim().toLowerCase()).filter((n) => staticByName.has(n))
            );
            let staticMatch = staticByName.get((raw.name || '').trim().toLowerCase());
            if (!staticMatch && raw.data && raw.data.image) {
              const candidates = staticList.filter((p) =>
                  normalizeAssetReference(this.getPersonaImage(p)) === normalizeAssetReference(raw.data.image)
                  && !claimedNames.has((p.title || p.name || '').trim().toLowerCase())
              );
              if (candidates.length === 1) staticMatch = candidates[0];
            }
            const savedImage = raw.data && raw.data.image;
            resolvedImage = (savedImage ? staticAssetPath(savedImage) : '')
              || (staticMatch ? this.getPersonaImage(staticMatch) : this.getPersonaImage({ name: undefined }));
          }
        } catch (error) {
          console.error('Failed to load persona:', error);
          raw = null;
        }
      }

      if (!raw) {
        this.persona = null;
        return;
      }

      this.persona = raw;
      this.populateForm(raw, resolvedImage);
    },

    populateForm(doc, resolvedImage) {
      const data = doc.data || {};
      const pc = data.personal_characteristics || data.personalCharacteristics || {};

      this.form = {
        image: staticAssetPath(resolvedImage || data.image || ''),
      };

      for (const field of this.fieldDefs) {
        const name = field.name;
        if (!name) continue;
        if (name === 'active_services') {
          this.form[name] = this.parseActiveServices(this.readDynamicFieldValue(data, pc, field));
          continue;
        }
        this.form[name] = this.readDynamicFieldValue(data, pc, field);
      }
    },

    readDynamicFieldValue(data, pc, field) {
      const name = field.name;
      const schemaKey = field.schema_key || name;
      const candidates = [...new Set([name, schemaKey, ...this.schemaLookupKeys(name), ...this.schemaLookupKeys(schemaKey)])];

      let raw;
      for (const key of candidates) {
        if (data[key] !== undefined && data[key] !== null && data[key] !== '') {
          raw = data[key];
          break;
        }
        if (pc && pc[key] !== undefined && pc[key] !== null && pc[key] !== '') {
          raw = pc[key];
          break;
        }
      }
      if (raw === undefined) {
        return this.isNumberField(field) ? null : '';
      }
      if (this.isDomainSingleSelect(name) || this.isDomainSingleSelect(schemaKey)) {
        return this.normalizeSelectValue(name, raw);
      }
      if (this.isNumberField(field) && raw !== '' && !Number.isNaN(Number(raw))) {
        return Number(raw);
      }
      return raw;
    },
    formatInterests(value) {
      if (Array.isArray(value)) return value.join(', ');
      return value ? String(value) : '';
    },

    previewValue(fieldName) {
      const v = this.form[fieldName];
      if (Array.isArray(v)) return v.length ? v.join(', ') : '—';
      return v !== null && v !== undefined && v !== '' ? v : '—';
    },

    selectImage(path) {
      this.form.image = path;
      this.imagePickerOpen = false;
    },

    async save() {
      const result = await this.$refs.formRef.validate();
      if (result && result.valid === false) return;

      this.saving = true;
      try {
        const mergedData = JSON.parse(JSON.stringify(this.persona.data || {}));

        for (const field of this.fieldDefs) {
          const key = field.schema_key || field.name;
          const value = this.form[field.name];
          if (value === undefined) continue;

          if (field.name === 'active_services') {
            mergedData[key] = Array.isArray(value) ? value.join(',') : value;
            continue;
          }
          if (field.name === 'age') {
            mergedData[key] = this.serializeAgeForSave(value);
            continue;
          }
          mergedData[key] = value;
        }

        if (this.domain === 'telco') {
          const charKey = mergedData.personal_characteristics
            ? 'personal_characteristics'
            : (mergedData.personalCharacteristics ? 'personalCharacteristics' : 'personal_characteristics');
          const pc = { ...(mergedData[charKey] || {}) };
          pc.age = mergedData.age;
          pc.occupation = mergedData.job_title || mergedData.occupation;
          pc.civil_status = mergedData.marital_status || mergedData.civil_status;
          pc.gender = mergedData.gender;
          pc.qualification = mergedData.education_level || mergedData.qualification;
          const incomeValue = mergedData.annual_income || mergedData.income;
          pc.income = incomeValue;
          pc.annual_income = incomeValue;
          mergedData[charKey] = pc;
        }

        if (this.form.image) {
          mergedData.image = normalizeAssetReference(this.form.image);
        }

        const schemaAttrs = Object.values(this.domainAttrMeta || {});
        const normalizedData = canonicalizePersonaSelectValues(schemaAttrs, mergedData);

        const name = this.displayName || this.persona.name;

        await create_or_update_persona(
          name,
          this.domain,
          normalizedData,
          this.personaId,
          this.persona.tags || [],
        );

        sessionStorage.removeItem('editPersonaRow');
        this.$router.push({ name: 'persona-management', query: { domain: this.domain, saved: '1' } });
      } catch (error) {
        console.error('Failed to save persona:', error);
        this.errorMessage = error?.response?.data?.detail || this.t('personamanagement.editError');
        this.errorSnackbar = true;
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
/* Editable=OFF fields render readonly (see isFieldEditable) so the value
   stays visible but cannot be changed. Vuetify does not style readonly on
   its own (only disabled is dimmed), so mark locked fields with the same
   opacity used for disabled controls elsewhere — without using disabled
   itself (which would also drop the field from tab order / form submit). */
:deep(.v-field:has(input:read-only)) {
  background-color: rgba(var(--v-theme-on-surface), 0.06);
}

:deep(.v-field) input:read-only {
  opacity: var(--v-disabled-opacity);
  cursor: not-allowed;
}

.edit-wrapper {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 1.5rem;
  align-items: start;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem 1.5rem;
}

.field-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #424242;
}

.persona-avatar {
  border: 3px solid #e0e0e0;
}

.preview-card {
  position: sticky;
  top: 1rem;
}

.preview-list {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem 1rem;
}

.preview-item {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.image-picker-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(72px, 1fr));
  gap: 0.75rem;
}

.image-picker-option {
  cursor: pointer;
  border: 3px solid transparent;
  transition: border-color 0.15s;
}

.image-picker-option:hover {
  border-color: #90caf9;
}

.image-picker-option--selected {
  border-color: #0D3B6E;
}

@media (max-width: 960px) {
  .edit-wrapper {
    grid-template-columns: 1fr;
  }
  .form-grid {
    grid-template-columns: 1fr;
  }
}
</style>
